import { NotRefOrderQueryParams } from "@saleor/createOrChangeNotRefContract/urls";
import { ICashsaleFormValues } from "@saleor/createOrder/components/CashSaleForm/types";
import {
  CpOrderUpdateInput,
  SplitCipOrderLineInputAfterCp,
  useAddCipSplitOrderAfterCpMutation,
  useCipChangeOrderAddNewOrderLineMutation,
  useCipChangeOrderUpdateMutation,
  useCpUpdateCreateOrderMutation,
  useSubmitCashSaleMutation,
  useSubmitCashSaleUpdateMutation
} from "@saleor/graphql";
import { UseNavigatorResult } from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import getErrorOutDated from "@saleor/utils/errors/outDated";
import { buildUrl } from "@saleor/utils/urls";
import { FormikProps } from "formik";
import moment from "moment-timezone";
import React, { Dispatch, SetStateAction } from "react";

import {
  getNotRefPreviewChangeOrderPath,
  getNotRefPreviewOrderPath
} from "../urls";
import {
  checkHasPriceAffectingChanges,
  cipAddItemDetailsPayload,
  cpOrderLinesMapping,
  deleteLocalOrderId,
  findChangeOrderCashSaleEditedField,
  prepareCashSalePayload,
  prepareHeaderPayload,
  prepareItemDetailsPayload,
  saveSplitItemLineAfterCPMapping,
  saveSplitItemOriginLineItemAfterCPMapping,
  submitCPOrderLineMapping
} from "../utils";

export interface ICPOrderProps {
  formikCPItemDetails: FormikProps<any>;
  setOpenDialogSplitSucess: Dispatch<SetStateAction<any>>;
  resetSubmitState: () => void;
  formikHeader: FormikProps<NotRefOrderHeader>;
  orderId: string;
  formikItemDetails: FormikProps<{ lines: ChangeNotRefOrderLine[] }>;
  formikCashSale: FormikProps<ICashsaleFormValues>;
  isCashSaleOrder?: boolean;
  isEditableCashSale?: boolean;
  navigate: UseNavigatorResult;
  isChangeFlow: boolean;
  currentAction: "none" | "add" | "split" | "edit";
  refetchChangeOrderData?: () => void;
  setRefetchInfo?: Dispatch<SetStateAction<any>>;
  setIsSynced?: (e: boolean) => void;
  setSubmitState: Dispatch<SetStateAction<any>>;
  changeCurrentAction?: (a: "none" | "add" | "split" | "edit") => void;
  params: NotRefOrderQueryParams;
}

function useCPOrder({
  formikCPItemDetails,
  setSubmitState,
  resetSubmitState,
  formikHeader,
  navigate,
  isChangeFlow,
  currentAction,
  orderId,
  formikItemDetails,
  formikCashSale,
  isCashSaleOrder,
  isEditableCashSale,
  refetchChangeOrderData,
  setOpenDialogSplitSucess,
  changeCurrentAction,
  params
}: ICPOrderProps) {
  const notify = useNotifier();

  const handleSubmitResponse = async (responseData, errors) => {
    // Submit CashSale
    let cashSaleStatus: string = null;
    if (isCashSaleOrder && responseData?.success && currentAction !== "split") {
      cashSaleStatus = await handleSubmitCashSale(responseData?.order?.id);
    }

    resetSubmitState();
    // Handle eOrderWarnings
    if (responseData?.eorWarnings?.length) {
      let eOrderWarning = "";
      responseData.eorWarnings.forEach(({ message }) => {
        eOrderWarning += `${message}\n`;
      });

      notify({
        title: "Warning E-Ordering",
        status: "warning",
        text: React.createElement(
          "div",
          null,
          React.createElement(
            "p",
            { style: { padding: "16px 12px" } },
            eOrderWarning
          )
        ),
        autohide: null
      });
    }
    if (responseData?.warningMessages?.length > 0) {
      const fmtMessage = item => {
        const title = item.order || item.itemNo;
        return title ? `${title} - ${item.message}` : item.message;
      };
      notify({
        title: "Warning SAP",
        status: "warning",
        apiMessage: responseData?.warningMessages?.map(fmtMessage).join("\n"),
        autohide: null
      });
    }
    if ((responseData?.order?.id || isChangeFlow) && responseData?.success) {
      const params: Record<string, string> = {};
      if (cashSaleStatus) {
        params.cashSaleStatus = cashSaleStatus;
      }

      if (!isChangeFlow) {
        deleteLocalOrderId(formikHeader?.values?.soldToCode);
        navigate(buildUrl(getNotRefPreviewOrderPath(orderId), params), {
          replace: true
        });
      } else if (currentAction !== "split") {
        params.rootPage = "changeOrder";
        navigate(buildUrl(getNotRefPreviewChangeOrderPath(orderId), params), {
          replace: true
        });
      } else {
        changeCurrentAction("edit");
        refetchChangeOrderData();
        setOpenDialogSplitSucess(true);
      }
    } else if (!responseData?.success) {
      if (responseData?.cpItemMessages?.length > 0) {
        const sortedLines = responseData?.cpItemMessages
          .slice()
          .sort((a, b) => a.itemNo - b.itemNo);
        const cpLines = cpOrderLinesMapping(
          sortedLines.filter(item => item && item?.showInPopup === true)
        );
        formikCPItemDetails.setFieldValue("lines", cpLines);

        if (currentAction === "split") {
          const lines = formikItemDetails?.values?.lines || [];
          sortedLines.forEach(line => {
            const splitItem = lines.find(
              item =>
                item?.itemNo === line?.itemNo ||
                item?.splitItemNo === line?.itemNo
            );
            if (splitItem) {
              splitItem.confirmDate = moment(line?.confirmDate, [
                "DD/MM/YYYY",
                "YYYY/MM/DD",
                "DD-MM-YYYY"
              ]).format("YYYY-MM-DD");
              splitItem.parentBom = line?.parentBom;
            }
          });
          formikItemDetails.setFieldValue("lines", lines);
        }
      }
      const errorOutDated = getErrorOutDated(responseData);
      if (errorOutDated) {
        responseData.errorOutDated = errorOutDated?.errorOutDated;
        if (currentAction === "split") {
          changeCurrentAction("edit");
        }
      }
      setSubmitState({
        state: "errorPopup",
        context: responseData
      });
    }
    if (errors) {
      setSubmitState({
        state: "errorPopup",
        context: errors
      });
    }
  };

  const getCpUpdateCreateOrderVariables = (): {
    input: CpOrderUpdateInput;
  } => ({
    input: {
      orderId,
      lines: submitCPOrderLineMapping(
        formikCPItemDetails?.values?.lines,
        formikItemDetails
      )
    }
  });

  const [CpUpdateCreateOrderMutation] = useCpUpdateCreateOrderMutation({
    onError: () => {
      //
    }
  });

  const getCpUpdateSplitOrderVariables = (): {
    soNo: string;
    isBom: boolean;
    originLineItems: SplitCipOrderLineInputAfterCp[];
    splitLineItems: SplitCipOrderLineInputAfterCp[];
  } => {
    const isBom = formikItemDetails.values?.lines?.some(
      item => item.splitOriginal === true && item.bomFlag === true
    );
    let prev = [...(formikItemDetails.values?.lines || [])];
    if (!params?.asc) {
      prev = prev.reverse();
    }
    return {
      soNo: orderId,
      isBom: isBom || false,
      originLineItems: saveSplitItemOriginLineItemAfterCPMapping(
        prev.filter(item => item.splitOriginal === true)
      ),
      splitLineItems: saveSplitItemLineAfterCPMapping(
        formikItemDetails.values?.lines.filter(item => item.isSplit === true)
      )
    };
  };

  const [AddCipSplitOrderAfterCpMutation] = useAddCipSplitOrderAfterCpMutation({
    onError: error => {
      notify({
        status: "error",
        apiMessage: error.message
      });
    }
  });

  const getUpdatecipChangeOrderVariables = () => {
    const headerDetailsVariables = prepareHeaderPayload(
      formikHeader?.values,
      formikHeader,
      orderId
    );
    headerDetailsVariables.cpNeed = false;
    return {
      id: orderId || "",
      input: {
        headerDetails: headerDetailsVariables,
        itemDetails: prepareItemDetailsPayload(
          formikItemDetails?.values?.lines,
          formikItemDetails
        )
      }
    };
  };

  const [CipChangeOrderUpdateMutation] = useCipChangeOrderUpdateMutation({
    onError: () => {
      //
    }
  });

  const getCipAddNewProductVariables = () => ({
    input: {
      headerDetails: { soNo: orderId, cpNeed: false },
      itemDetails: cipAddItemDetailsPayload(formikItemDetails?.values?.lines)
    }
  });

  const [cipAddNewProductUpdate] = useCipChangeOrderAddNewOrderLineMutation({
    onError: () => {
      //
    }
  });

  const handleCPResultDateConfirm = () => {
    setSubmitState({ state: "processing", context: null });
    if (!isChangeFlow) {
      CpUpdateCreateOrderMutation({
        variables: getCpUpdateCreateOrderVariables()
      })
        .then(({ data, errors }) => {
          const responseData = data?.cpUpdateCreateOrder;
          handleSubmitResponse(responseData, errors);
        })
        .catch(() => {
          resetSubmitState();
        });
    } else if (currentAction === "split") {
      AddCipSplitOrderAfterCpMutation({
        variables: getCpUpdateSplitOrderVariables()
      })
        .then(({ data, errors }) => {
          const responseData = data?.addCipSplitOrderLineAfterCp;
          handleSubmitResponse(responseData, errors);
        })
        .catch(() => {
          resetSubmitState();
        });
    } else if (currentAction === "add") {
      cipAddNewProductUpdate({
        variables: getCipAddNewProductVariables()
      })
        .then(({ data, errors }) => {
          const responseData = data?.cipChangeOrderAddNewOrderLine;
          handleSubmitResponse(responseData, errors);
        })
        .catch(() => {
          resetSubmitState();
        });
    } else {
      CipChangeOrderUpdateMutation({
        variables: getUpdatecipChangeOrderVariables()
      })
        .then(({ data, errors }) => {
          const responseData = data?.cipChangeOrderUpdate;
          handleSubmitResponse(responseData, errors);
        })
        .catch(() => {
          resetSubmitState();
        });
    }
  };

  const [submitCashSaleCreate] = useSubmitCashSaleMutation();
  const [submitCashSaleUpdate] = useSubmitCashSaleUpdateMutation();

  const handleSubmitCashSale = async (orderId: string) => {
    if (isChangeFlow) {
      const { isEdited } = findChangeOrderCashSaleEditedField(formikCashSale);
      const hasPriceAffectingChanges = checkHasPriceAffectingChanges(
        formikItemDetails
      );
      const hasDataChanged = isEdited || hasPriceAffectingChanges;

      if (hasDataChanged && isEditableCashSale) {
        const payload = prepareCashSalePayload(orderId, formikCashSale, {
          forceUpdate: hasPriceAffectingChanges
        });
        const { data } = await submitCashSaleUpdate(payload);
        return data?.submitCashSaleUpdate?.cashSaleStatus;
      }
    } else {
      const payload = prepareCashSalePayload(orderId, formikCashSale);
      const { data } = await submitCashSaleCreate(payload);
      return data?.submitCashSale?.cashSaleStatus;
    }
  };

  return { handleCPResultDateConfirm, handleSubmitResponse };
}

export default useCPOrder;
